/**
 * Intel reports — resolution phase 10 (RB §2.2, §9.1, §9.2).
 *
 * M1 left this to M2, and deliberately made it easy: every `GameEvent` already
 * carries a `visibleTo` tag list assigned **at emission**, by the only code that
 * knows why something is visible. A report is therefore a filter, not a
 * computation.
 *
 * Two things it is not allowed to be:
 *
 *  1. **A payload dump.** Event payloads carry true asset ids — `subject`,
 *     `target`, `contact` — and shipping those to a player defeats the whole alias
 *     scheme (decision M2-D2). Every id-bearing field is translated: a friendly
 *     asset keeps its real id, and everything else becomes the viewer's alias for
 *     it. The translation is default-deny, so a field nobody thought about is
 *     aliased rather than leaked.
 *  2. **A roll log.** `RollRecord` carries the per-roll seed and the target's true
 *     modifiers. It goes to the GM's roll inspector (§11.1) and the audit trail,
 *     never to a player. The itemised modifier list is a teaching device in the
 *     *pre-commit* order builder (M5), computed client-side from the shared tables
 *     — not a post-hoc reveal of what the enemy's stack looked like.
 *
 * The highest-value line in the whole report is `ORDER_INVALIDATED`. §7.3: "why
 * did nothing happen?" is the worst possible after-action outcome, so the reason
 * is carried verbatim and the client renders it first.
 */
import { aliasTableFor } from './project.js';
export const INTEL_CATEGORIES = [
    'ORDER',
    'MISSION',
    'DETECTION',
    'FORCE',
    'ENVIRONMENT',
    'CYBER',
    'ENGAGEMENT',
    'ESCALATION',
    'OTHER',
];
const CATEGORY_OF = Object.freeze({
    TURN_RESOLVED: 'OTHER',
    ORDER_INVALIDATED: 'ORDER',
    MISSION_RESOLVED: 'MISSION',
    DETECTION_CHANGED: 'DETECTION',
    CHARACTERIZED: 'DETECTION',
    PHANTOM_CREATED: 'DETECTION',
    PHANTOM_REMOVED: 'DETECTION',
    PHANTOM_EXPIRED: 'DETECTION',
    SEARCH_EXHAUSTED: 'DETECTION',
    ASSET_MOVED: 'FORCE',
    TRANSFER_DEPARTED: 'FORCE',
    TRANSFER_ARRIVED: 'FORCE',
    DELTAV_SPENT: 'FORCE',
    DELTAV_EXHAUSTED: 'FORCE',
    STATUS_CHANGED: 'FORCE',
    CONTROL_LOST: 'FORCE',
    CONTROL_RESTORED: 'FORCE',
    TTC_REASSIGNED: 'FORCE',
    DEPLOYED: 'FORCE',
    SCENARIO_DEFECT: 'OTHER',
    CYBER_ACCESS_CHANGED: 'CYBER',
    CYBER_ACTION_DETECTED: 'CYBER',
    CYBER_PAYLOAD_DEPLOYED: 'CYBER',
    CYBER_HUNT_REPORT: 'CYBER',
    CYBER_HARDENING_CHANGED: 'CYBER',
    CYBER_ISOLATION_CHANGED: 'CYBER',
    CYBER_RECOVERY_ACCELERATED: 'CYBER',
    ORDER_HIJACKED: 'ORDER',
    EFFECT_APPLIED: 'FORCE',
    EFFECT_LAPSED: 'FORCE',
    EFFECT_ATTRIBUTED: 'ESCALATION',
    PROTECTION_ESTABLISHED: 'FORCE',
    WEAPON_LAUNCHED: 'ENGAGEMENT',
    WEAPON_ARRIVED: 'ENGAGEMENT',
    WEAPON_INTERCEPTED: 'ENGAGEMENT',
    INTERCEPTION_FAILED: 'ENGAGEMENT',
    ENGAGEMENT_RESOLVED: 'ENGAGEMENT',
    EVADE_TRIGGERED: 'ENGAGEMENT',
    TERMINAL_ACQUISITION_FAILED: 'ENGAGEMENT',
    ASSET_DESTROYED: 'ENGAGEMENT',
    BELIEF_CHANGED: 'DETECTION',
    MW_EVENT_CREATED: 'ENGAGEMENT',
    MW_STAGE_REVEALED: 'ENGAGEMENT',
    MW_IDENTIFIED: 'ENGAGEMENT',
    MW_RELEASED: 'ENGAGEMENT',
    MW_BENIGN_ENGAGED: 'ENGAGEMENT',
    CONJUNCTION: 'ENVIRONMENT',
    DEBRIS_CHANGED: 'ENVIRONMENT',
    WEATHER_FORECAST: 'ENVIRONMENT',
    ESCALATION_BANKED: 'ESCALATION',
    ESCALATION_APPLIED: 'ESCALATION',
    ESCALATION_DECAYED: 'ESCALATION',
    ESCALATION_LEVEL_CHANGED: 'ESCALATION',
    ROE_VIOLATION: 'ESCALATION',
});
/**
 * Payload keys that hold an identifier. Anything listed here is translated;
 * anything not listed is copied only if it is a scalar (see `sanitisePayload`).
 */
const ID_FIELDS = new Set([
    'asset',
    'subject',
    'target',
    'contact',
    'parent',
    'sub',
    'station',
    'shadow',
    'sustainedBy',
    // M3
    'launcher',
    'attacker',
    'victim',
    'jammer',
    'protector',
    'interceptor',
]);
const ID_ARRAY_FIELDS = new Set(['found', 'assets', 'contacts', 'targets']);
function tagsFor(viewer) {
    return [viewer.team, `${viewer.team}:${viewer.cell}`];
}
/** Does this event reach this seat? GM is handled by `gmIntelReport`. */
export function visibleToSeat(event, viewer) {
    const tags = tagsFor(viewer);
    return event.visibleTo.some((tag) => tags.includes(tag));
}
/**
 * Default-deny: an id is emitted verbatim only when it names an asset the viewer's
 * own team owns. Everything else — enemy assets, phantoms, ids from an event type
 * written after this file — becomes an alias.
 */
function translateId(translator, value) {
    return translator.friendly.has(value) ? value : translator.aliases.alias(value);
}
function isScalar(v) {
    return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}
function sanitisePayload(payload, translator) {
    const fields = {};
    let actor = null;
    let contact = null;
    for (const [key, value] of Object.entries(payload)) {
        if (ID_FIELDS.has(key) && typeof value === 'string') {
            const translated = translateId(translator, value);
            fields[key] = translated;
            if (key === 'asset' || key === 'parent')
                actor ??= translator.friendly.has(value) ? value : null;
            if (key === 'subject' || key === 'target' || key === 'contact')
                contact ??= translated;
            continue;
        }
        if (ID_ARRAY_FIELDS.has(key) && Array.isArray(value)) {
            const translated = value
                .filter((v) => typeof v === 'string')
                .map((v) => translateId(translator, v));
            fields[key] = translated.join(', ');
            contact ??= translated[0] ?? null;
            continue;
        }
        // Nested objects and functions are dropped rather than walked. Nothing in the
        // event vocabulary needs them, and a recursive copier is exactly how an
        // untranslated id gets through in eighteen months.
        if (!isScalar(value))
            continue;
        // Belt and braces over `ID_FIELDS`: a string that names an asset is aliased
        // whatever key it arrived under.
        fields[key] =
            typeof value === 'string' && translator.everyAssetId.has(value)
                ? translateId(translator, value)
                : value;
    }
    return { fields, actor, contact };
}
function entryFor(event, translator) {
    const { fields, actor, contact } = sanitisePayload(event.payload, translator);
    const outcome = typeof event.payload.outcome === 'string' ? event.payload.outcome : null;
    return {
        seq: event.seq,
        phase: event.phase,
        category: CATEGORY_OF[event.type] ?? 'OTHER',
        type: event.type,
        outcome,
        actor,
        contact,
        fields,
        // Note what is absent: `event.roll`. Never projected to a player.
    };
}
/**
 * Build one seat's report for a resolved turn.
 *
 * `world` is the state the turn resolved *into*, which is what the alias table
 * must be built from — a contact found this turn has to be nameable in the report
 * that announces it.
 */
export function buildIntelReport(events, viewer, world, options, turn) {
    const friendly = new Set(Object.values(world.assets)
        .filter((a) => a.owner === viewer.team)
        .map((a) => a.id));
    const translator = {
        friendly,
        everyAssetId: new Set(Object.keys(world.assets)),
        aliases: aliasTableFor(world, viewer.team, options.salt),
    };
    const entries = events
        .filter((e) => e.turn === turn)
        .filter((e) => visibleToSeat(e, viewer))
        .sort((a, b) => a.seq - b.seq)
        .map((e) => entryFor(e, translator));
    return {
        turn,
        viewer: { team: viewer.team, cell: viewer.cell },
        entries,
    };
}
/**
 * RB §2.2: "Skipped during the first turn." There is no turn 0 to report on, and
 * an empty report panel says something different from no report panel.
 */
export function hasIntelReport(turn) {
    return turn > 1;
}
/** The GM's log: untranslated, unfiltered, rolls included (§3.3 invariant 4). */
export function gmIntelReport(events, turn) {
    return events
        .filter((e) => e.turn === turn)
        .sort((a, b) => a.seq - b.seq)
        .map((e) => ({
        seq: e.seq,
        phase: e.phase,
        type: e.type,
        visibleTo: e.visibleTo,
        payload: e.payload,
        roll: e.roll,
    }));
}
//# sourceMappingURL=intel.js.map